/**
 * MOCK/TEMPORARY — shared plumbing for the fixture-backed API modules.
 *
 * ============================================================================
 * EVERY MODULE IN src/api/ IS CURRENTLY A MOCK. NONE OF THEM PERFORM A NETWORK
 * REQUEST. They exist so that UI code never embeds an assumption about where
 * data comes from: components call `songsApi.getSongs()` today and will still
 * call `songsApi.getSongs()` once the Python backend is live.
 *
 * Replacing a mock means rewriting the body of one function in one file. See
 * docs/api-contract.md for the proposed request/response shapes.
 * ============================================================================
 */
import { MOCK_LATENCY_MS, USE_MOCK_API } from '../config';

/**
 * Resolves after a short delay so loading states and spinners are genuinely
 * exercised in the mock, rather than resolving synchronously and hiding bugs
 * that only appear against a real, slow network.
 *
 * `ms` is passed explicitly where the prototype used a specific duration — the
 * simulated generation timings are part of the current UX and are preserved
 * verbatim (see generationApi).
 */
export function mockDelay<T>(value: T, ms: number = MOCK_LATENCY_MS): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

/**
 * Guard placed at the top of every mock call.
 *
 * If someone sets VITE_API_BASE_URL expecting real endpoints, the mocks would
 * otherwise keep quietly serving fixtures and the app would look like it was
 * talking to the backend. Failing loudly is the point.
 */
export function assertMock(operation: string): void {
  if (!USE_MOCK_API) {
    throw new Error(
      `${operation} is still a MOCK implementation, but VITE_API_BASE_URL is set. ` +
        'Implement the real request in src/api/ before pointing the frontend at a backend.'
    );
  }
}

/*
  ApiError USED TO BE DEFINED HERE, and it has moved to ./errors.ts.

  WHY: this file is headed "MOCK/TEMPORARY" and every line of it is deleted the day the
  modules in this directory perform real requests. The error contract has the opposite
  lifetime — a real `fetch` wrapper constructs it, stores branch on it, components render
  it — so the durable half of the boundary was living in the file scheduled for deletion.

  RE-EXPORTED RATHER THAN MOVED OUTRIGHT, so that the ~10 modules importing `ApiError`
  from './mock' keep working and this stays a pure relocation with no call-site churn.
  New code should import from './errors', which is where the rest of the contract
  (`toApiError`, `apiErrorKind`, the predicates) lives.
*/
export { ApiError } from './errors';
export type { ApiErrorCode, ApiErrorKind, ApiErrorShape } from './errors';
